import { useState } from "react";
import { X, Wifi, Loader2, RotateCcw, Power, Printer, Search } from "lucide-react";
import { useTranslation } from "../../i18n";

// Network & device control for a Raspberry Pi print agent (Admin › Printers).
//
// The device is behind the site's NAT, so nothing here talks to it directly:
// every action is queued on the BE (POST /printers/agents/command) and
// collected by the agent's ~15s control poll, which then posts the outcome
// back. The modal just renders the agent row's NetInfo / PendingCommand /
// CommandResult — the page polls faster while it's open so outcomes appear
// promptly. Same mechanism and the same WiFi strings as the light agents'
// panel in Settings › Sorting › Lighting.

export interface AgentNetInfo {
    ssid?: string | null;
    signal?: number | null;
    ips?: Record<string, string>;
    error?: string;
    power_save?: string;
    build?: number;
    agent_version?: string;
    printer?: {
        name?: string; online?: boolean; host?: string | null; model?: string | null; via?: string | null; iface?: string;
    } | null;
    at?: string;
}
export interface AgentCommandPublic { id: string; type: string; requested_at?: string | null }
export interface AgentCommandResult {
    id: string;
    type: string;
    ok: boolean;
    detail?: string;
    finished_at?: string;
    networks?: { ssid: string; signal: number; security: string; in_use: boolean }[];
}

interface Props {
    /** Agent id — shown in the title and used in confirmations. */
    agentId: string;
    online: boolean;
    netInfo: AgentNetInfo | null;
    pending: AgentCommandPublic | null;
    result: AgentCommandResult | null;
    readOnly?: boolean;
    /** Queue a command: { action: "scan" } | { action: "connect", ssid, psk, connect_now } | { action: "restart" | "reboot" | "test_print" | "rediscover" } */
    onQueue: (body: Record<string, unknown>) => void | Promise<void>;
    onClose: () => void;
}

const muted = { color: "var(--color-text-muted, #6b7280)" } as const;

const AgentNetworkModal = ({ agentId, online, netInfo, pending, result, readOnly = false, onQueue, onClose }: Props) => {
    const { t } = useTranslation();
    const [ssid, setSsid] = useState("");
    const [psk, setPsk] = useState("");
    const busy = !!pending || readOnly;

    const connect = (connectNow: boolean) => {
        const s = ssid.trim();
        if (!s) return;
        if (psk && (psk.length < 8 || psk.length > 63)) return;
        onQueue({ action: "connect", ssid: s, psk, connect_now: connectNow });
    };
    const device = (action: string, confirmKey?: "agent_confirm_restart" | "agent_confirm_reboot") => {
        if (confirmKey && !window.confirm(t(confirmKey, { id: agentId }))) return;
        onQueue({ action });
    };
    const printer = netInfo?.printer ?? null;
    const isWifiResult = result?.type === "wifi_scan" || result?.type === "wifi_connect";

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
                <div className="modal-card-header">
                    <strong>{t("agent_network_title", { id: agentId })}</strong>
                    <button className="modal-close" onClick={onClose}><X size={18} /></button>
                </div>
                <div className="modal-card-body" style={{ fontSize: 13, display: "flex", flexDirection: "column", gap: 12 }}>
                    {/* Current connection + printer, as last self-reported by the device. */}
                    <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>{t("pas_wifi_current")}</div>
                        {netInfo ? (
                            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <span>
                                    <Wifi size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                                    {netInfo.ssid
                                        ? <>{netInfo.ssid}{netInfo.signal != null && <span style={muted}> — {netInfo.signal}%</span>}</>
                                        : <span style={muted}>{netInfo.error ?? "—"}</span>}
                                </span>
                                {Object.entries(netInfo.ips ?? {}).map(([iface, ip]) => (
                                    <span key={iface} style={{ ...muted, fontFamily: "monospace", fontSize: 12 }}>{iface}: {ip}</span>
                                ))}
                                {printer && (
                                    <span>
                                        <Printer size={13} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                                        {t("agent_printer")}: {printer.online
                                            ? <>{printer.model || printer.name}<span style={muted}> @ {printer.host}</span></>
                                            : <span style={{ color: "#d97706" }}>{t("printer_offline")}</span>}
                                    </span>
                                )}
                                {netInfo.agent_version && <span style={{ ...muted, fontSize: 12 }}>v{netInfo.agent_version}</span>}
                            </div>
                        ) : (
                            <div style={muted}>{t("pas_wifi_not_reported")}</div>
                        )}
                    </div>

                    {/* Device actions — one click each, collected by the next poll. */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => onQueue({ action: "scan" })}>
                            <Wifi size={14} /> {t("pas_wifi_scan")}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => device("test_print")}>
                            <Printer size={14} /> {t("agent_test_print")}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => device("rediscover")}>
                            <Search size={14} /> {t("agent_rediscover")}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => device("restart", "agent_confirm_restart")}>
                            <RotateCcw size={14} /> {t("agent_restart")}
                        </button>
                        <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => device("reboot", "agent_confirm_reboot")}>
                            <Power size={14} /> {t("agent_reboot")}
                        </button>
                    </div>

                    {/* Command in flight — the device collects it on its ~15s poll. */}
                    {pending && (
                        <div style={{ display: "flex", alignItems: "center", gap: 8, ...muted }}>
                            <Loader2 size={14} style={{ animation: "fl-spin 1s linear infinite" }} />
                            {t("pas_wifi_waiting")}
                            {!online && <span style={{ color: "#d97706" }}>{t("pas_wifi_offline_warn")}</span>}
                        </div>
                    )}

                    {/* Last outcome: a banner, or the scan's network list. */}
                    {result?.type === "wifi_connect" && (
                        <div style={{ color: result.ok ? "#16a34a" : "#dc2626" }}>
                            {t(result.ok ? "pas_wifi_saved_ok" : "pas_wifi_failed", { detail: result.detail ?? "" })}
                        </div>
                    )}
                    {result?.type === "wifi_scan" && !result.ok && (
                        <div style={{ color: "#dc2626" }}>{t("pas_wifi_scan_failed", { detail: result.detail ?? "" })}</div>
                    )}
                    {result && !isWifiResult && (
                        <div style={{ color: result.ok ? "#16a34a" : "#dc2626" }}>
                            {t(result.ok ? "agent_command_done" : "agent_command_failed", { detail: result.detail ?? "" })}
                        </div>
                    )}
                    {result?.type === "wifi_scan" && result.ok && (
                        <div>
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>{t("pas_wifi_networks")}</div>
                            {(result.networks ?? []).length === 0 && <div style={muted}>{t("pas_wifi_no_networks")}</div>}
                            <div style={{ display: "flex", flexDirection: "column", gap: 2, maxHeight: 180, overflowY: "auto" }}>
                                {(result.networks ?? []).map(n => (
                                    <button key={n.ssid} type="button"
                                        onClick={() => { setSsid(n.ssid); setPsk(""); }}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 8, padding: "4px 6px", fontSize: 13,
                                            background: ssid === n.ssid ? "var(--color-surface-alt, #f3f4f6)" : "none",
                                            border: "1px solid var(--color-border, #e5e7eb)", borderRadius: 6, cursor: "pointer", textAlign: "left",
                                        }}>
                                        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.ssid}</span>
                                        {n.in_use && <span style={{ color: "#16a34a", fontSize: 11 }}>{t("pas_wifi_in_use")}</span>}
                                        {!n.security && <span style={{ ...muted, fontSize: 11 }}>{t("pas_wifi_open")}</span>}
                                        <span style={{ ...muted, fontFamily: "monospace", fontSize: 12 }}>{n.signal}%</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Join / save a network — picked from the scan or typed by hand. */}
                    <div style={{ borderTop: "1px solid var(--color-border, #e5e7eb)", paddingTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ ...muted, fontSize: 12 }}>{t("pas_wifi_manual")}</div>
                        <input className="admin-settings-input" type="text" value={ssid} placeholder={t("pas_wifi_ssid")} maxLength={32}
                            onChange={e => setSsid(e.target.value)} />
                        <input className="admin-settings-input" type="password" value={psk} placeholder={t("pas_wifi_password")} maxLength={63}
                            onChange={e => setPsk(e.target.value)} />
                        <div style={{ ...muted, fontSize: 11 }}>{t("pas_wifi_save_hint")}</div>
                        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 4 }}>
                            <button type="button" className="btn btn-primary btn-sm" disabled={!ssid.trim() || busy} onClick={() => connect(false)}>
                                {t("pas_wifi_save")}
                            </button>
                            <button type="button" className="btn btn-secondary btn-sm" disabled={!ssid.trim() || busy} onClick={() => connect(true)}>
                                {t("pas_wifi_save_switch")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentNetworkModal;
